import { useState, type ReactNode } from "react";
import { Filter } from "lucide-react";
import { format, parseISO } from "date-fns";

import { Button } from "@/components/ui/button";
import { InlineDateField } from "@/components/waste/inline-date-field";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";

import {
  PRICE_MAX,
  WEIGHT_MAX,
  WASTE_FILTER_DEFAULTS,
  useWasteFilters,
  toFilterDraft,
  type DatePreset,
  type WasteFilters,
  type WasteFilterDraft,
} from "@/hooks/use-waste-filters";
import type { SiteSummary } from "@/hooks/use-active-menus";
import type { Split } from "@/hooks/use-explore-state";
import { SERVICE_TIMES, serviceTimeLabel } from "@/lib/service-times";
import { WASTE_TYPES, wasteTypeLabel } from "@/lib/waste-types";
import {
  EARLIEST_DAY,
  EARLIEST_YEAR,
  isBeforeEarliest,
  parseDay,
  parseTime,
} from "@/lib/date-input";
import { useTranslation } from "@/i18n/use-translation";
import { currencySymbol } from "@/lib/format";
import { zonedDay } from "@/lib/zoned-day";

const SENTINEL_ALL = "__all__";

// Explore split options (POS2-1000) — Dish removed in POS2-851. "none" is a real
// option (no split), so this control has no "All" sentinel.
/** Literal keys, resolved at render — a module-level constant that called `t()` would capture
 * the locale at import (POS2-1134 CP3a). `dish` is deliberately absent: filtering and splitting
 * the same dimension collapses the chart to one series. */
export const SPLIT_OPTIONS: { value: Split; labelKey: string }[] = [
  { value: "none", labelKey: "waste.split.none" },
  { value: "kitchen", labelKey: "waste.split.kitchen" },
  { value: "service", labelKey: "waste.split.service" },
  { value: "reason", labelKey: "waste.split.reason" },
];

type Option = { value: string; label: string };

type Kitchen = { id: string; name: string };

// Sensors are devices; the optional site_id lets the sensor list narrow to the drafted
// site. Consumers that don't scope by site simply omit it (all sensors show).
type SensorDevice = { device_serial: string; name: string | null; site_id?: string | null };

// Tier values map to classification_level (number). L4 = most specific → L1 = unidentified.
// Literal keys resolved at render — a module-level constant that called `t()` would capture the
// locale at import (POS2-1134 CP3a).
const TIER_OPTION_KEYS: { value: string; labelKey: string }[] = [
  { value: "4", labelKey: "waste.tierOptionL4" },
  { value: "3", labelKey: "waste.tierOptionL3" },
  { value: "2", labelKey: "waste.tierOptionL2" },
  { value: "1", labelKey: "waste.tierOptionL1" },
];

const CONFIRMATION_OPTION_KEYS: { value: string; labelKey: string }[] = [
  { value: "confirmed", labelKey: "waste.confirmed" },
  { value: "unconfirmed", labelKey: "waste.unconfirmed" },
];

// The filter fields this popover can render. Consumers pass a subset via `fields`
// to show only the relevant ones, in their own order. "split" is Explore-only (a
// live chart dimension, not a draft filter) and is off by default. Default = all
// the draft filters (Dashboard / Transactions).
export type FilterField =
  | "dates"
  | "times"
  | "price"
  | "weight"
  | "site"
  | "kitchen"
  | "waste_type"
  | "sensor"
  | "food"
  | "service_time"
  | "tier"
  | "confirmation"
  | "split";

const ALL_FILTER_FIELDS: FilterField[] = [
  "dates",
  "times",
  "price",
  "weight",
  "site",
  "kitchen",
  "waste_type",
  "sensor",
  "food",
  "service_time",
  "tier",
  "confirmation",
];

type Props = {
  sites: SiteSummary[];
  // Site-scoped kitchens for the selected site (the consumer scopes via useKitchens).
  kitchens: Kitchen[];
  // "Sensor" is the product-facing name for a device (filters by device_serial). Carry
  // site_id to scope the sensor list to the drafted site (POS2-880).
  devices: SensorDevice[];
  // "Food" source (identified food) is resolved by the consumer at adoption; empty until then.
  foods?: Option[];
  // ISO currency code (e.g. "EUR") from the org→site→kitchen config — drives the Price
  // symbol. Optional: only the Price field reads it, so pages without that field (Explore)
  // can omit it.
  currency?: string;
  // Must match the page's own useWasteFilters default (the homepage passes "this_week")
  // or the two hook instances compute different windows for the same URL.
  defaultPreset?: DatePreset;
  // Same rule for a single-site page's default site (POS2-1039): it feeds the timezone
  // entity, so an instance that doesn't get it resolves the ORG's zone while the page
  // resolves the SITE's — and the picker seeds land on the wrong civil day whenever the
  // two zones' days differ. Omit only on multi-site pages, which have no default site.
  defaultSiteId?: string | null;
  // Single-site mode (POS2-913, Dashboard): the site selector never offers "All sites"
  // — exactly one site is always chosen — and the always-set site doesn't count as an
  // active filter. Other pages omit this and keep the multi-site "All sites" behaviour.
  singleSite?: boolean;
  // Fired when the popover opens/closes so the consumer can defer expensive option
  // loads (the Food dropdown scans the org's whole waste history — POS2-960).
  onOpenChange?: (open: boolean) => void;
  // Which filter fields to render (default = all). Explore passes its own subset +
  // ordering, including the Explore-only "split" control (POS2-880/1000).
  fields?: FilterField[];
  // Explore-only Split control (POS2-1000; POS2-884). When passed (with "split" in
  // `fields`), a Split dropdown renders in the popover. `value` is the applied split
  // (seeds the draft on open); the chosen value commits on Apply — in the same
  // setSearchParams update as the filters — so it behaves like every other filter.
  split?: { value: Split };
  /** This page's default window as inclusive civil days, from `useWasteFilters`. Shown as the
   * date placeholders, so an empty field reads as "the default" rather than "unbounded"
   * (POS2-1049). Required: every page has a default, and a page that showed `YYYY-MM-DD` instead
   * left the reader unable to tell what window was active. */
  datesDefault: { from: string; to: string };
};

/**
 * Shared waste-page filter UI (POS2-753). Owns no state of its own — it reads
 * and writes the URL-backed `useWasteFilters` store, so Dashboard and
 * Transactions share one filter state without prop drilling.
 */
export function FilterPopover({
  sites,
  kitchens,
  devices,
  foods = [],
  currency,
  defaultPreset,
  defaultSiteId,
  singleSite = false,
  onOpenChange,
  fields = ALL_FILTER_FIELDS,
  split,
  datesDefault,
}: Props) {
  const { locale, t } = useTranslation();
  const { filters, dayRange, scopeSiteId, timezone, applyFilters, clear } = useWasteFilters(
    defaultPreset,
    defaultSiteId,
  );
  const symbol = currencySymbol(locale, currency ?? "GBP");
  const activeCount = countActive(filters, singleSite, fields, scopeSiteId);

  // Seed the date pickers with the active window so the chosen range is visible; emptying them
  // then means this page's default, which is what the placeholders show (POS2-1049). A preset
  // window (e.g. the 7-day default) carries no explicit dates, so derive them from the window's
  // civil days — the SITE's (POS2-1039), then written back at the viewer's midnight like every
  // other picked date. An all-time window stays empty.
  function buildDraft(): WasteFilterDraft {
    const d = toFilterDraft(filters);
    if (!d.date_from && !d.date_to && filters.preset !== "all") {
      const to = dayRange.to ?? zonedDay(new Date(), timezone);
      if (dayRange.from) d.date_from = dayToIso(dayRange.from, "start");
      d.date_to = dayToIso(to, "end");
    }
    return d;
  }

  // Edits stage in a local draft and only commit on "Apply" (no auto-apply).
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<WasteFilterDraft>(buildDraft);
  // Explore's Split rides the same draft/Apply cycle (POS2-884): staged locally,
  // committed on Apply with the filters (a separate write would race — see apply()).
  const [splitDraft, setSplitDraft] = useState<Split>(split?.value ?? "none");

  // Re-sync the draft to the applied filters on open so unapplied edits are
  // discarded and reopening reflects the live state.
  function handleOpenChange(next: boolean) {
    if (next) {
      setDraft(buildDraft());
      setSplitDraft(split?.value ?? "none");
    }
    setOpen(next);
    onOpenChange?.(next);
  }

  function update(patch: Partial<WasteFilterDraft>) {
    setDraft((d) => ({ ...d, ...patch }));
  }

  // An end date before the start date matches nothing, so it is a typo rather than a query —
  // block Apply and say why, as the transactions date-range popover already does (POS2-1068).
  // Gated on the field being shown: a page without it can't produce the inversion, and locking
  // Apply over an unrendered value would be a dead end.
  const invertedRange =
    fields.includes("dates") &&
    draft.date_from != null &&
    draft.date_to != null &&
    new Date(draft.date_to) < new Date(draft.date_from);

  // A year the estate has no data for, refused the same way and for the same reason (POS2-1279):
  // the native picker takes any year and so does `parseDay`, and what comes back is nonsense
  // rather than an error. Gated on the field being shown, as the inversion above is.
  const tooEarly =
    fields.includes("dates") &&
    (isBeforeEarliest(isoToDay(draft.date_from)) || isBeforeEarliest(isoToDay(draft.date_to)));

  function apply() {
    // Commit the split in the same update as the filters (the "none" default drops
    // the param, matching useExploreState). Omit entirely when there's no split control.
    const extra = split ? { split: splitDraft === "none" ? null : splitDraft } : undefined;
    applyFilters(draft, extra);
    setOpen(false);
  }

  /** Return every filter to this page's defaults — the panel's one reset (POS2-1049).
   *
   * `clear()` empties the URL, so each field falls back to the page default: the Dashboard to this
   * week, Transactions to last 7 days, Explore to its granularity default.
   *
   * The site survives on a **single-site** page, because there it is scope rather than a filter —
   * exactly the rule `countActive` already uses, and the reason it is not in the badge. Blanking it
   * looked broken (single-site mode has no "All sites" option, so the control rendered empty) and
   * an Apply afterwards would have stripped `site_id` from the URL, sending the next page org-wide.
   * On a multi-site page the site IS a chosen filter, so a reset returns it to "All sites". */
  function resetToDefault() {
    clear();
    setDraft({
      ...toFilterDraft(WASTE_FILTER_DEFAULTS),
      site_id: singleSite ? filters.site_id : null,
    });
  }

  // Each field's block, rendered in the order the consumer lists them in `fields`
  // (so Explore can order site → sensor → waste type independently of other pages).
  const renderField = (f: FilterField): ReactNode => {
    switch (f) {
      case "dates":
        return (
          <div key="dates">
            <div className="grid grid-cols-2 gap-3">
              <InlineDateField
                label={t("waste.startDate")}
                kind="date"
                placeholder={datesDefault.from}
                value={isoToDay(draft.date_from)}
                min={EARLIEST_DAY}
                onCommit={(raw) => update({ date_from: commitDay(raw, "start") })}
                isValid={isValidDay}
                testId="filter-date-from"
              />
              <InlineDateField
                label={t("waste.endDate")}
                kind="date"
                placeholder={datesDefault.to}
                value={isoToDay(draft.date_to)}
                min={EARLIEST_DAY}
                onCommit={(raw) => update({ date_to: commitDay(raw, "end") })}
                isValid={isValidDay}
                testId="filter-date-to"
              />
            </div>
            {(invertedRange || tooEarly) && (
              <p className="mt-1.5 text-xs text-red-600" data-testid="filter-date-range-error">
                {invertedRange
                  ? t("waste.dateRangeInvalid")
                  : t("waste.dateTooEarly", { year: EARLIEST_YEAR })}
              </p>
            )}
          </div>
        );
      case "times":
        return (
          <div key="times">
            <div className="grid grid-cols-2 gap-3">
              <InlineDateField
                label={t("waste.startTime")}
                kind="time"
                placeholder="00:00"
                value={draft.time_from ?? ""}
                onCommit={(raw) => update({ time_from: commitTime(raw) })}
                isValid={isValidTime}
                testId="filter-time-from"
              />
              <InlineDateField
                label={t("waste.endTime")}
                kind="time"
                placeholder="23:59"
                value={draft.time_to ?? ""}
                onCommit={(raw) => update({ time_to: commitTime(raw) })}
                isValid={isValidTime}
                testId="filter-time-to"
              />
            </div>
          </div>
        );
      case "price":
        return (
          <RangeField
            key="price"
            label={t("waste.price")}
            min={0}
            max={PRICE_MAX}
            step={10}
            value={[draft.price_min, draft.price_max]}
            prefix={symbol}
            onChange={(lo, hi) => update({ price_min: lo, price_max: hi })}
            testId="filter-price"
          />
        );
      case "weight":
        return (
          <RangeField
            key="weight"
            label={t("waste.weightFilter")}
            min={0}
            max={WEIGHT_MAX}
            step={1}
            value={[draft.weight_min, draft.weight_max]}
            suffix="kg"
            onChange={(lo, hi) => update({ weight_min: lo, weight_max: hi })}
            testId="filter-weight"
          />
        );
      case "site":
        return sites.length > 1 ? (
          <FilterSelect
            key="site"
            label={t("waste.site")}
            placeholder={t("waste.allSites")}
            value={draft.site_id}
            // Sensors are site-scoped, so a site change clears any chosen sensor.
            onChange={(v) => update({ site_id: v, device_serial: null })}
            options={sites.map((s) => ({ value: s.id, label: s.name }))}
            includeAll={!singleSite}
            testId="filter-site"
          />
        ) : null;
      case "kitchen":
        return (
          <FilterSelect
            key="kitchen"
            label={t("waste.kitchen")}
            placeholder={t("waste.allKitchens")}
            value={draft.kitchen_id}
            onChange={(v) => update({ kitchen_id: v })}
            options={kitchens.map((k) => ({ value: k.id, label: k.name }))}
            testId="filter-kitchen"
          />
        );
      case "waste_type":
        return (
          <FilterSelect
            key="waste_type"
            label={t("waste.wasteType")}
            placeholder={t("waste.allTypes")}
            value={draft.waste_type}
            onChange={(v) => update({ waste_type: v })}
            options={WASTE_TYPES.map((wt) => ({ value: wt, label: wasteTypeLabel(t, wt) }))}
            testId="filter-waste-type"
          />
        );
      case "sensor": {
        // Narrow to the drafted site (live, before Apply) so you can't pick a sensor from
        // another site. Devices without a site_id (unscoped consumers) always pass through.
        const sensorOptions = devices.filter(
          (d) => d.site_id == null || !draft.site_id || d.site_id === draft.site_id,
        );
        return (
          <FilterSelect
            key="sensor"
            label={t("waste.sensor")}
            placeholder={t("waste.allSensors")}
            value={draft.device_serial}
            onChange={(v) => update({ device_serial: v })}
            options={sensorOptions.map((d) => ({
              value: d.device_serial,
              label: d.name ?? d.device_serial,
            }))}
            testId="filter-sensor"
          />
        );
      }
      case "food":
        return (
          <FilterSelect
            key="food"
            label={t("waste.food")}
            placeholder={t("waste.allFoods")}
            value={draft.food}
            onChange={(v) => update({ food: v })}
            options={foods}
            testId="filter-food"
          />
        );
      case "service_time":
        return (
          <FilterSelect
            key="service_time"
            label={t("waste.serviceTime")}
            placeholder={t("waste.anyService")}
            value={draft.service_time}
            onChange={(v) => update({ service_time: v })}
            options={SERVICE_TIMES.map((s) => ({ value: s, label: serviceTimeLabel(t, s) }))}
            testId="filter-service-time"
          />
        );
      case "tier":
        return (
          <FilterSelect
            key="tier"
            label={t("waste.tier")}
            placeholder={t("waste.allTiers")}
            value={draft.classification_level == null ? null : String(draft.classification_level)}
            onChange={(v) => update({ classification_level: v == null ? null : Number(v) })}
            options={TIER_OPTION_KEYS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
            testId="filter-tier"
          />
        );
      case "confirmation":
        return (
          <FilterSelect
            key="confirmation"
            label={t("waste.confirmation")}
            placeholder={t("waste.allStatuses")}
            value={
              draft.is_confirmed == null ? null : draft.is_confirmed ? "confirmed" : "unconfirmed"
            }
            onChange={(v) => update({ is_confirmed: v == null ? null : v === "confirmed" })}
            options={CONFIRMATION_OPTION_KEYS.map((o) => ({
              value: o.value,
              label: t(o.labelKey),
            }))}
            testId="filter-confirmation"
          />
        );
      case "split":
        // Explore only: a chart dimension staged in the local draft and committed on
        // Apply (POS2-884), so it behaves like the other filters.
        if (!split) return null;
        return (
          <div key="split">
            <p className="mb-1.5 text-xs text-gray-400">{t("waste.splitBy")}</p>
            <Select value={splitDraft} onValueChange={(v) => setSplitDraft(v as Split)}>
              <SelectTrigger className="h-8 w-full text-xs" data-testid="filter-split">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SPLIT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {t(o.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="relative size-8"
          aria-label={t("waste.filters")}
        >
          <Filter className="size-4" />
          {activeCount > 0 && (
            <span
              className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-gray-900 text-[10px] text-white"
              data-testid="filter-count"
            >
              {activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="flex max-h-[var(--radix-popover-content-available-height)] w-80 flex-col p-0"
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <p className="text-sm font-medium text-gray-900">{t("waste.filters")}</p>
          {/* Gated on the badge's count, NOT `hasActiveFilters`: the latter counts the seeded
              `site_id`, so on a single-site page it is always true and this would offer to reset a
              panel that is already at its defaults. `countActive` excludes the always-selected site
              in `singleSite` mode, so the control appears exactly when the badge does. The two
              signals differ on purpose — don't unify them. */}
          {activeCount > 0 && (
            <button
              type="button"
              className="text-xs text-gray-400 hover:text-gray-600"
              onClick={resetToDefault}
              data-testid="filter-reset"
            >
              {t("waste.resetToDefault")}
            </button>
          )}
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {fields.map((f) => renderField(f))}
        </div>

        <div className="border-t border-gray-100 p-3">
          <Button
            size="sm"
            className="h-8 w-full text-xs"
            onClick={apply}
            disabled={invertedRange || tooEarly}
            data-testid="filter-apply"
          >
            {t("waste.apply")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function RangeField({
  label,
  min,
  max,
  step,
  value,
  prefix = "",
  suffix = "",
  onChange,
  testId,
}: {
  label: string;
  min: number;
  max: number;
  step: number;
  value: [number, number];
  prefix?: string;
  suffix?: string;
  onChange: (low: number, high: number) => void;
  testId: string;
}) {
  const [low, high] = value;
  // Editable min/max — type a value (empty resets that side to its bound). The
  // slider and the inputs share the same draft state.
  function commit(raw: string, edge: "low" | "high") {
    const fallback = edge === "low" ? min : max;
    const n = raw === "" ? fallback : Number(raw);
    if (!Number.isFinite(n)) return;
    if (edge === "low") onChange(clamp(n, min, high), high);
    else onChange(low, clamp(n, low, max));
  }
  return (
    <div data-testid={testId}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-xs text-gray-400">{label}</p>
        <div className="flex items-center gap-1">
          <NumberBox
            prefix={prefix}
            suffix={suffix}
            value={low}
            onChange={(raw) => commit(raw, "low")}
            testId={`${testId}-min`}
          />
          <span className="text-xs text-gray-400">–</span>
          <NumberBox
            prefix={prefix}
            suffix={suffix}
            value={high}
            onChange={(raw) => commit(raw, "high")}
            testId={`${testId}-max`}
          />
        </div>
      </div>
      <Slider
        min={min}
        max={max}
        step={step}
        value={value}
        onValueChange={(v) => onChange(v[0], v[1])}
        aria-label={label}
      />
    </div>
  );
}

function NumberBox({
  prefix,
  suffix,
  value,
  onChange,
  testId,
}: {
  prefix: string;
  suffix: string;
  value: number;
  onChange: (raw: string) => void;
  testId: string;
}) {
  return (
    <span className="inline-flex items-center rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-xs text-gray-600 focus-within:border-gray-300 focus-within:ring-1 focus-within:ring-blue-500">
      {prefix && <span className="text-gray-400">{prefix}</span>}
      <input
        type="number"
        inputMode="numeric"
        min={0}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-14 bg-transparent text-right tabular-nums text-gray-900 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
        data-testid={testId}
      />
      {suffix && <span className="ml-0.5 text-gray-400">{suffix}</span>}
    </span>
  );
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

function FilterSelect({
  label,
  placeholder,
  value,
  onChange,
  options,
  testId,
  includeAll = true,
}: {
  label: string;
  placeholder: string;
  value: string | null;
  onChange: (value: string | null) => void;
  options: Option[];
  testId: string;
  // When false, the "All …" (clear) option is dropped so a value must always be
  // chosen — used by the single-site dashboard site selector (POS2-913).
  includeAll?: boolean;
}) {
  return (
    <div>
      <p className="mb-1.5 text-xs text-gray-400">{label}</p>
      <Select
        value={value ?? SENTINEL_ALL}
        onValueChange={(v) => onChange(v === SENTINEL_ALL ? null : v)}
      >
        <SelectTrigger className="h-8 w-full text-xs" data-testid={testId}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {includeAll && <SelectItem value={SENTINEL_ALL}>{placeholder}</SelectItem>}
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** Number of filter fields currently set to a non-default value. In single-site
 * mode the always-selected site isn't an "active filter", so it's excluded.
 *
 * Nor is a site that came from context rather than a choice (`scopeSiteId`, POS2-1049): a site
 * manager whose switcher pins their one site had the badge reading 1 on a freshly reset panel, and
 * the reset control offering to reset nothing. Same scope-is-not-a-filter rule the reset itself
 * uses. A site the user picked, or one restored from the shared store, still counts. */
function countActive(
  f: WasteFilters,
  singleSite = false,
  fields: FilterField[] = ALL_FILTER_FIELDS,
  scopeSiteId: string | null = null,
): number {
  const on = (field: FilterField) => fields.includes(field);
  let n = 0;
  if (on("dates") && (f.date_from || f.date_to)) n++;
  if (on("times") && (f.time_from || f.time_to)) n++;
  if (on("price") && (f.price_min !== 0 || f.price_max !== PRICE_MAX)) n++;
  if (on("weight") && (f.weight_min !== 0 || f.weight_max !== WEIGHT_MAX)) n++;
  if (on("site") && f.site_id && !singleSite && f.site_id !== scopeSiteId) n++;
  if (on("kitchen") && f.kitchen_id) n++;
  if (on("waste_type") && f.waste_type) n++;
  if (on("sensor") && f.device_serial) n++;
  if (on("food") && f.food) n++;
  if (on("service_time") && f.service_time) n++;
  if (on("tier") && f.classification_level != null) n++;
  if (on("confirmation") && f.is_confirmed != null) n++;
  return n;
}

/** "YYYY-MM-DD" date input value ↔ ISO timestamp at the local day boundary. */
function isoToDay(iso: string | null): string {
  return iso ? format(parseISO(iso), "yyyy-MM-dd") : "";
}

function dayToIso(day: string, edge: "start" | "end"): string | null {
  if (!day) return null;
  const time = edge === "start" ? "T00:00:00.000" : "T23:59:59.999";
  return new Date(`${day}${time}`).toISOString();
}

/** A real calendar date in any of the typed forms `parseDay` accepts. */
function isValidDay(raw: string): boolean {
  return parseDay(raw) != null;
}

function commitDay(raw: string, edge: "start" | "end"): string | null {
  const day = parseDay(raw);
  return day ? dayToIso(day, edge) : null;
}

/** A 24-hour time-of-day; the committed value is always HH:MM, the backend's pattern. */
function isValidTime(raw: string): boolean {
  return parseTime(raw) != null;
}

function commitTime(raw: string): string | null {
  return parseTime(raw);
}
